// --- React
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
// --- Config & Utils
import * as columnDefs from 'datastage/components/AGGrid/config/columns-def';
import { fetchReadyProductsPage } from '../../../../utils/fetchReadyProductsPage';
// --- Hooks
import { useReadyProductsActions } from 'datastage/store/readyProducts/useReadyProducts';
import { useLoading } from 'datastage/utils/hooks/useLoading';
import { useToasts } from 'datastage/utils/hooks/useToasts';


/**
 * Custom hook for managing utilities related to the Ready Products grid
 *
 * Features:
 * - Manages sorting state and logic for the grid
 * - Fetches sorted data and updates the store accordingly
 * - Prepares column definitions with sorting controls integrated
 *
 * @param {Object} options - Optional parameters for the hook
 * @param {Array} options.columnHandlers - Array of handler functions to be passed to column definitions
 * @returns {Object} Object containing setSort function and prepared columns for the grid
 */
export function useReadyProductsGridUtils({ columnHandlers = [] } = {}) {
    const { updateReadyProducts } = useReadyProductsActions();
    const { readyProductRange } = useSelector((s) => s.readyProducts);
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { showErrorToast } = useToasts();
    const [sortedColId, setSortedColId] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');

    // Handle sorting: update state, fetch sorted data, update products
    const setSort = useCallback(
        async (colId, direction) => {
            setSortedColId(colId);
            setSortDirection(direction);

            const customSort = [colId, direction.toUpperCase()];
            setLoadingTrue();
            try {
                const data = await fetchReadyProductsPage(readyProductRange, customSort);
                if (data?.code) {
                    showErrorToast(data?.message || 'Failed to fetch ready products.');
                    return;
                }
                if (data) {
                    updateReadyProducts({
                        readyProducts: data?.content ?? [],
                        totalPages: data?.totalPages ?? 0,
                        totalItems: data?.totalElements ?? 0,
                        currentPage: data?.pageable?.pageNumber ?? 0,
                        readyProductSort: customSort,
                    });
                }
            } catch (error) {
                showErrorToast(error?.message || 'Failed to fetch ready products.');
                console.error('Error fetching ready products:', error);
            } finally {
                setLoadingFalse();
            }
        },
        [
            readyProductRange,
            updateReadyProducts,
            setLoadingTrue,
            setLoadingFalse,
            showErrorToast,
        ],
    );

    // Prepare column definitions with sorting controls
    const baseColumns = columnDefs['readyProducts'](...columnHandlers)['default'];

    const columns = useMemo(
        () =>
            baseColumns.map((col) => ({
                ...col,
                headerComponentParams: {
                    ...col.headerComponentParams,
                    setSort,
                    sortedColId,
                    sortDirection,
                },
            })),
        [setSort, sortedColId, sortDirection, baseColumns],
    );

    return { setSort, columns };
}
